import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../config";
import { FaPlay, FaYoutube, FaMicrophone, FaBookOpen, FaPenFancy, FaArrowRight, FaChevronLeft, FaChevronRight, FaSearch, FaTree, FaSeedling, FaTrophy, FaRegCopy, FaCheck } from "react-icons/fa";

// ─── Helpers ───────────────────────────────────────────────

const ytThumb = (videoId) =>
  videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;

const fmtDate = (iso, tz = "America/Chicago") => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { timeZone: tz, month: "short", day: "numeric", year: "numeric" });
};

// ─── Animated Counter ──────────────────────────────────────

const AnimatedNumber = ({ target, label, icon, delay = 0 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1600;
          const step = Math.max(1, Math.floor(target / (duration / 16)));
          let current = 0;
          setTimeout(() => {
            const timer = setInterval(() => {
              current += step;
              if (current >= target) { current = target; clearInterval(timer); }
              setCount(current);
            }, 16);
          }, delay);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, delay]);

  return (
    <div ref={ref} className="flex flex-col items-center group">
      <div className="text-kk-gold text-3xl mb-3 group-hover:scale-110 transition-transform duration-300">{icon}</div>
      <div className="text-4xl md:text-5xl font-bold text-white tabular-nums">{count.toLocaleString()}+</div>
      <div className="text-kk-gold-light/80 text-sm mt-2 tracking-wide uppercase">{label}</div>
    </div>
  );
};

// ─── Horizontal Scroll Carousel ────────────────────────────

const ScrollCarousel = ({ children, itemWidth = 300 }) => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * itemWidth * 2, behavior: "smooth" });

  return (
    <div className="relative group/carousel">
      <button
        onClick={() => scroll(-1)}
        className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-kk-ink/90 border border-kk-gold/20 text-kk-gold w-10 h-10 rounded-full items-center justify-center hover:bg-kk-gold/10 hover:border-kk-gold/40 transition-all hidden md:flex"
      >
        <FaChevronLeft />
      </button>
      <div
        ref={ref}
        className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
      >
        {children}
      </div>
      <button
        onClick={() => scroll(1)}
        className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-kk-ink/90 border border-kk-gold/20 text-kk-gold w-10 h-10 rounded-full items-center justify-center hover:bg-kk-gold/10 hover:border-kk-gold/40 transition-all hidden md:flex"
      >
        <FaChevronRight />
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// HOME PAGE
// ═══════════════════════════════════════════════════════════

const Home = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({ episodes: 0, storytellers: 0, stories: 0, writers: 0, performances: 0 });
  const [episodes, setEpisodes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("weekly");
  const [isLoading, setIsLoading] = useState(true);
  const searchTimeout = useRef(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [
        { count: epCount },
        { count: stCount },
        { count: storCount },
        { count: wrCount },
        { count: perfCount },
        { data: epData },
        { data: sessData },
      ] = await Promise.all([
        supabase.from("episodes").select("*", { count: "exact", head: true }),
        supabase.from("storytellers").select("*", { count: "exact", head: true }),
        supabase.from("stories").select("*", { count: "exact", head: true }),
        supabase.from("writers").select("*", { count: "exact", head: true }),
        supabase.from("performances").select("*", { count: "exact", head: true }),
        supabase.from("episodes")
          .select("episode_id, video_id, title, publish_date, scheduled_start")
          .not("video_id", "is", null)
          .order("publish_date", { ascending: false }),
        supabase.from("sessions")
          .select("session_id, session_title, session_honor_title_telugu, session_honor_title_english, poster_image_url, youtube_live_url, day")
          .order("day", { ascending: true })
          .order("session_id", { ascending: true }),
      ]);

      setStats({ episodes: epCount || 0, storytellers: stCount || 0, stories: storCount || 0, writers: wrCount || 0, performances: perfCount || 0 });
      setEpisodes(epData || []);
      setSessions(sessData || []);
    } catch (err) {
      console.error("Home data load error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Search ───

  const handleSearch = (text) => {
    setSearchText(text);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!text.trim()) { setSearchResults([]); setIsSearching(false); return; }
    setIsSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        // Fold ళ్ల (retroflex + dental) -> ళ్ళ (geminate retroflex) in the
        // query so a visitor who types the common ళ్ల variant still matches
        // the normalized data (which we standardized to ళ్ళ). E.g. typing
        // ముళ్లపూడి finds ముళ్ళపూడి.
        const normalized = text.trim().replace(/ళ్ల/g, "ళ్ళ");
        const term = `%${normalized}%`;
        // The schema doesn't keep a flat (story_title, writer_name) on
        // performances, so we run three filtered queries and merge:
        //   1. storytellers — direct ilike on performances.storyteller_name_telugu
        //   2. story titles — stories.title_telugu → story_ids → performances
        //   3. writers     — writers.name_telugu → stories → performances
        const [{ data: byStoryteller }, { data: storyMatches }, { data: writerMatches }] = await Promise.all([
          supabase.from("performances")
            .select("performance_id, story_id, episode_id, category, storyteller_name_telugu, story_start_sec")
            .ilike("storyteller_name_telugu", term)
            .order("performance_id", { ascending: false })
            .limit(20),
          supabase.from("stories").select("story_id").ilike("title_telugu", term).limit(40),
          supabase.from("writers").select("writer_id").ilike("name_telugu", term).limit(40),
        ]);

        // Stories from writer matches
        let storyIdsByWriter = [];
        const writerIds = (writerMatches || []).map((w) => w.writer_id);
        if (writerIds.length) {
          const { data: storiesByWriter } = await supabase
            .from("stories").select("story_id").in("writer_id", writerIds);
          storyIdsByWriter = (storiesByWriter || []).map((s) => s.story_id);
        }

        const allStoryIds = [
          ...new Set([
            ...(storyMatches || []).map((s) => s.story_id),
            ...storyIdsByWriter,
          ]),
        ];

        let byStoryOrWriter = [];
        if (allStoryIds.length) {
          const { data } = await supabase.from("performances")
            .select("performance_id, story_id, episode_id, category, storyteller_name_telugu, story_start_sec")
            .in("story_id", allStoryIds)
            .order("performance_id", { ascending: false })
            .limit(20);
          byStoryOrWriter = data || [];
        }

        // Merge by performance_id, dedup
        const merged = new Map();
        [...(byStoryteller || []), ...byStoryOrWriter].forEach((p) => merged.set(p.performance_id, p));
        const perfs = Array.from(merged.values()).slice(0, 12);

        if (perfs.length === 0) {
          setSearchResults([]);
          return;
        }

        // Enrich with story title, writer name, episode video_id
        const sIds = [...new Set(perfs.map((p) => p.story_id).filter(Boolean))];
        const eIds = [...new Set(perfs.map((p) => p.episode_id).filter(Boolean))];
        const [storiesRes, episodesRes] = await Promise.all([
          sIds.length
            ? supabase.from("stories").select("story_id, title_telugu, writer_id").in("story_id", sIds)
            : Promise.resolve({ data: [] }),
          eIds.length
            ? supabase.from("episodes").select("episode_id, video_id, publish_date").in("episode_id", eIds)
            : Promise.resolve({ data: [] }),
        ]);
        const sMap = Object.fromEntries((storiesRes.data || []).map((s) => [s.story_id, s]));
        const eMap = Object.fromEntries((episodesRes.data || []).map((e) => [e.episode_id, e]));
        const wIds = [...new Set((storiesRes.data || []).map((s) => s.writer_id).filter(Boolean))];
        const writersRes = wIds.length
          ? await supabase.from("writers").select("writer_id, name_telugu").in("writer_id", wIds)
          : { data: [] };
        const wMap = Object.fromEntries((writersRes.data || []).map((w) => [w.writer_id, w]));

        const enriched = perfs.map((p) => {
          const story = sMap[p.story_id];
          const writer = story?.writer_id ? wMap[story.writer_id] : null;
          const episode = eMap[p.episode_id];
          return {
            performance_id: p.performance_id,
            episode_id: p.episode_id,
            publish_date: episode?.publish_date || null,
            video_id: episode?.video_id || null,
            category: p.category,
            story_title: story?.title_telugu || "—",
            writer_name: writer?.name_telugu || "—",
            storyteller_name: p.storyteller_name_telugu || "—",
            youtube_url: episode?.video_id ? `https://www.youtube.com/watch?v=${episode.video_id}` : null,
            start_sec: p.story_start_sec || 0,
          };
        });
        setSearchResults(enriched);
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setIsSearching(false);
      }
    }, 400);
  };

  const openPerformance = (p) => {
    if (p.youtube_url) {
      const url = p.start_sec ? `${p.youtube_url}&t=${p.start_sec}` : p.youtube_url;
      window.open(url, "_blank");
    }
  };

  // ─── Copy search results to clipboard ───
  // Format (one header + one line per story):
  //   BPlusWithBhaskar | Kathakala | <term> | Stories
  //   MM-DD-YYYY | <story> | <writer> | సమర్పణ : <storyteller> | <short YT link>
  // The link uses the project-standard short form youtu.be/<id>?t=<sec>.
  const formatDateMMDDYYYY = (isoDate) => {
    if (!isoDate) return "—";
    const [y, m, d] = isoDate.split("-");
    return y && m && d ? `${m}-${d}-${y}` : isoDate;
  };

  const shortYouTubeLink = (p) => {
    // No https:// prefix — most platforms (WhatsApp, YouTube, etc.)
    // auto-linkify youtu.be/… and it keeps the line shorter.
    if (!p.video_id) return "";
    return p.start_sec
      ? `youtu.be/${p.video_id}?t=${p.start_sec}`
      : `youtu.be/${p.video_id}`;
  };

  const buildSearchResultsText = () => {
    const header = `BPlusWithBhaskar | Kathakala | ${searchText.trim()} | Stories`;
    const lines = searchResults.map((p) =>
      [
        formatDateMMDDYYYY(p.publish_date),
        p.story_title || "—",
        p.writer_name || "—",
        `సమర్పణ : ${p.storyteller_name || "—"}`,
        shortYouTubeLink(p),
      ].join(" | ")
    );
    return [header, "", ...lines].join("\n");
  };

  const handleCopyResults = async () => {
    const text = buildSearchResultsText();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older/insecure-context browsers
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (e) { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Separate sessions by day for Utsavam tab
  const day1Sessions = useMemo(() => sessions.filter((s) => s.day === 1), [sessions]);
  const day2Sessions = useMemo(() => sessions.filter((s) => s.day === 2), [sessions]);

  // ─── Loading ───

  if (isLoading) {
    return (
      <div className="min-h-screen bg-kk-ink flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-kk-gold/30 border-t-kk-gold rounded-full animate-spin mx-auto mb-6" />
          <p className="font-krushna text-kk-gold text-2xl">కథాకళ</p>
        </div>
      </div>
    );
  }

  // ─── Render ───

  return (
    <div className="min-h-screen bg-kk-ink text-white overflow-x-hidden">

      {/* ═══ SEARCH (first thing under the rotating header banner) ═══ */}
      <section className="pt-6 md:pt-8 pb-4 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-center text-kk-gold-light/80 text-base md:text-lg tracking-[0.2em] uppercase mb-4 font-medium">
            Find a Story · కథ కోసం వెతకండి
          </p>
          <div className="relative">
            <FaSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-kk-gold/70 text-2xl" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search storytellers, stories, writers …"
              className="w-full bg-white/[0.06] border border-kk-gold/35 rounded-full py-5 md:py-6 pl-16 pr-6 text-kk-cream text-xl md:text-2xl placeholder-kk-gold-light/60 focus:outline-none focus:border-kk-gold/70 focus:bg-white/[0.09] focus:shadow-[0_0_36px_rgba(212,168,67,0.18)] transition-all duration-300 shadow-[0_0_24px_rgba(212,168,67,0.06)]"
            />
            {isSearching && (
              <div className="absolute right-6 top-1/2 -translate-y-1/2 w-6 h-6 border-2 border-kk-gold/30 border-t-kk-gold rounded-full animate-spin" />
            )}
          </div>

          {searchText.trim() && searchResults.length > 0 && (
            <div className="mt-3 bg-kk-ink/95 border border-kk-gold/20 rounded-2xl overflow-hidden shadow-2xl">
              <div className="max-h-[480px] overflow-y-auto">
                {searchResults.map((p) => (
                  <div
                    key={p.performance_id}
                    onClick={() => openPerformance(p)}
                    className="flex items-center gap-4 px-6 py-4 hover:bg-white/5 cursor-pointer border-b border-white/5 transition-colors"
                  >
                    <FaPlay className="text-kk-gold/50 text-sm flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-krushna text-kk-cream text-lg md:text-xl truncate">{p.story_title || "కథ"}</p>
                      <p className="text-kk-gold-light/60 text-sm md:text-base truncate">
                        {p.writer_name || "—"} &middot; {p.storyteller_name || "—"}
                      </p>
                    </div>
                    {p.category && (
                      <span className="text-xs px-2.5 py-1 bg-kk-gold/10 text-kk-gold/80 rounded-full flex-shrink-0">{p.category}</span>
                    )}
                  </div>
                ))}
              </div>
              {/* Copy-all footer bar */}
              <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-kk-gold/20 bg-white/[0.03]">
                <span className="text-kk-gold-light/50 text-sm">
                  {searchResults.length} {searchResults.length === 1 ? "story" : "stories"}
                </span>
                <button
                  onClick={handleCopyResults}
                  title="Copy all results to clipboard"
                  className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border transition-colors ${
                    copied
                      ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/10"
                      : "border-kk-gold/35 text-kk-gold-light/90 hover:bg-kk-gold/10 hover:border-kk-gold/60"
                  }`}
                >
                  {copied ? <FaCheck className="text-xs" /> : <FaRegCopy className="text-xs" />}
                  {copied ? "Copied!" : "Save / Copy"}
                </button>
              </div>
            </div>
          )}
          {searchText.trim() && !isSearching && searchResults.length === 0 && (
            <p className="text-center text-kk-gold-light/40 text-base mt-4">No results found</p>
          )}
        </div>
      </section>

      {/* ═══ UTSAVAM + STIRATU + JEEDIGUNTA HERO CARDS ═══ */}
      <section className="relative py-12 md:py-16 px-6 bg-gradient-to-b from-kk-ink via-kk-maroon-deep/20 to-kk-ink">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-6">

            {/* ── Utsavam Card ── */}
            <Link
              to="/sessions"
              className="group relative overflow-hidden rounded-2xl border border-kk-gold/20 hover:border-kk-gold/50 transition-all duration-500 hover:shadow-[0_0_40px_rgba(212,168,67,0.15)]"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src="/kathakala_utsavam_cover.png"
                  alt="కథాకళ ఉత్సవం 2026"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-kk-ink via-kk-ink/40 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <h2 className="font-krushna text-2xl md:text-3xl text-kk-cream mb-1">కథాకళ ఉత్సవం 2026</h2>
                <p className="text-kk-gold-light/70 text-sm mb-3">World Storytelling Festival &middot; 24-Hour Global Telugu Storytelling Marathon</p>
                <span className="inline-flex items-center gap-2 bg-gradient-to-r from-kk-gold to-kk-amber text-kk-ink font-bold px-5 py-2 rounded-full text-sm group-hover:shadow-[0_0_20px_rgba(212,168,67,0.4)] transition-all">
                  Watch Sessions <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

            {/* ── Stiratu Sankalpam Card ── */}
            <a
              href="https://sritaru.kathakala.us"
              target="_blank"
              rel="noreferrer"
              className="group relative overflow-hidden rounded-2xl border border-green-700/25 hover:border-green-500/50 bg-gradient-to-br from-green-950/60 via-green-900/30 to-kk-ink transition-all duration-500 hover:shadow-[0_0_40px_rgba(34,197,94,0.1)]"
            >
              <div className="p-6 md:p-8 flex flex-col justify-center h-full min-h-[260px]">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-green-800/30 border border-green-600/20 rounded-full p-3">
                    <FaTree className="text-green-400 text-2xl" />
                  </div>
                  <div>
                    <h2 className="font-krushna text-2xl md:text-3xl text-green-300">శ్రీతరు సంకల్పం</h2>
                    <p className="text-green-400/50 text-xs">Sri Taru Sankalpam</p>
                  </div>
                </div>

                <div className="flex items-end gap-4 mb-4">
                  <div>
                    <div className="text-4xl md:text-5xl font-bold text-green-300 tabular-nums">15,000+</div>
                    <div className="text-green-400/60 text-xs mt-0.5">Trees Planted</div>
                  </div>
                  <div className="flex-1 mx-2">
                    <div className="h-2 bg-green-900/50 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full" style={{width: "30%"}} />
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-400/70">50,000</div>
                    <div className="text-green-400/40 text-xs">Goal</div>
                  </div>
                </div>

                <p className="text-green-200/50 text-sm mb-4">
                  చేయి చేయి కలుపుతాం! పచ్చని ప్రపంచాన్ని నిలుపుతాం!
                </p>
                <span className="inline-flex items-center gap-2 self-start bg-green-800/30 border border-green-600/20 text-green-300 font-semibold px-5 py-2 rounded-full text-sm group-hover:bg-green-700/30 group-hover:border-green-500/30 transition-all">
                  <FaSeedling className="text-xs" /> Be Part of It <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </a>

            {/* ── Jeedigunta Kathakala Puraskaralu Card ── */}
            <Link
              to="/jeedigunta"
              className="group relative overflow-hidden rounded-2xl border border-kk-gold/30 hover:border-kk-gold/70 bg-gradient-to-br from-kk-maroon-deep/80 via-kk-maroon/50 to-kk-ink transition-all duration-500 hover:shadow-[0_0_40px_rgba(212,168,67,0.18)]"
            >
              <div className="p-6 md:p-7 flex flex-col justify-center h-full min-h-[260px]">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-kk-gold/15 border border-kk-gold/40 rounded-full p-3">
                    <FaTrophy className="text-kk-gold text-2xl" />
                  </div>
                  <div>
                    <h2 className="font-krushna text-2xl md:text-3xl text-kk-gold-light leading-tight">
                      జీడిగుంట కథాకళ
                    </h2>
                    <h2 className="font-krushna text-xl md:text-2xl text-kk-gold-light/85 leading-tight">
                      పురస్కారాలు
                    </h2>
                    <p className="text-kk-cream/55 text-xs italic font-serif mt-0.5">
                      Jeedigunta Kathakala Puraskaralu
                    </p>
                  </div>
                </div>

                <p className="text-kk-cream/75 text-sm mb-4 leading-relaxed">
                  Annual recognition of excellence in Telugu storytelling — winners
                  by jury, peers, and audience.
                </p>

                <div className="flex items-center gap-3 text-kk-gold/80 text-xs mb-4">
                  <span className="px-2 py-1 bg-kk-gold/10 border border-kk-gold/30 rounded">
                    2024 · Inaugural
                  </span>
                  <span className="px-2 py-1 bg-kk-gold/10 border border-kk-gold/30 rounded">
                    2026 · 2nd Cycle
                  </span>
                </div>

                <span className="inline-flex items-center gap-2 self-start bg-gradient-to-r from-kk-gold to-kk-amber text-kk-ink font-bold px-5 py-2 rounded-full text-sm group-hover:shadow-[0_0_20px_rgba(212,168,67,0.4)] transition-all">
                  Browse Winners <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

          </div>
        </div>
      </section>

      {/* ═══ STATS BAR ═══ */}
      <section className="relative py-10 md:py-14 bg-gradient-to-b from-kk-ink via-kk-maroon-deep/30 to-kk-ink">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-3 md:grid-cols-5 gap-6 md:gap-4">
            <AnimatedNumber target={stats.episodes} label="Episodes" icon={<FaYoutube />} delay={0} />
            <AnimatedNumber target={stats.storytellers} label="Storytellers" icon={<FaMicrophone />} delay={100} />
            <AnimatedNumber target={stats.performances} label="Performances" icon={<FaPlay />} delay={200} />
            <AnimatedNumber target={stats.stories} label="Stories" icon={<FaBookOpen />} delay={300} />
            <AnimatedNumber target={stats.writers} label="Writers" icon={<FaPenFancy />} delay={400} />
          </div>
        </div>
      </section>

      {/* ═══ WEEKLY EPISODES heading ═══ */}
      <section className="pt-8 pb-6 px-6">
        <h2 className="font-krushna text-kk-gold text-xl text-center mb-6">ప్రతి వారం ప్రసారాలు — Weekly Episodes</h2>
      </section>

      {/* ═══ EPISODES CAROUSEL ═══ */}
      <section className="pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollCarousel itemWidth={200}>
            {episodes.map((ep) => (
              <div key={ep.episode_id} className="min-w-[180px] md:min-w-[200px] snap-start flex-shrink-0">
                <div
                  onClick={() => navigate(`/episodes/${ep.episode_id}`)}
                  className="group cursor-pointer"
                >
                  <div className="relative rounded-lg overflow-hidden border border-kk-gold/15 group-hover:border-kk-gold/40 transition-all duration-300">
                    <img
                      src={ytThumb(ep.video_id)}
                      alt={ep.title || `Episode ${ep.episode_id}`}
                      className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                      <div className="bg-red-600 rounded-full p-2 opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300 shadow-lg">
                        <FaPlay className="text-white text-xs ml-0.5" />
                      </div>
                    </div>
                  </div>
                  <p className="text-kk-cream/70 text-xs mt-2 line-clamp-2 overflow-hidden group-hover:text-kk-gold-light transition-colors leading-snug">
                    {ep.title || `Episode ${ep.episode_id}`}
                  </p>
                  <p className="text-kk-gold-light/35 text-[10px] mt-0.5">{fmtDate(ep.publish_date || ep.scheduled_start)}</p>
                </div>
              </div>
            ))}
          </ScrollCarousel>
          <p className="text-center text-kk-gold-light/25 text-xs mt-4">
            {episodes.length} episodes &middot; New episode every Saturday at 8 PM CST
          </p>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-white/5 pt-10 pb-8 px-6 bg-gradient-to-b from-kk-ink to-kk-maroon-deep/10">
        <div className="max-w-5xl mx-auto">

          {/* Footer links */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/5">
            <div className="text-center md:text-left">
              <h3 className="font-krushna text-xl text-kk-cream">కథాకళ</h3>
              <p className="text-kk-gold-light/30 text-xs">The Art of Storytelling | B+ With Bhaskar</p>
            </div>
            <div className="flex items-center gap-5">
              <a href="https://www.youtube.com/@BPlusWithBhaskar" target="_blank" rel="noreferrer" className="text-white/25 hover:text-kk-gold transition-colors text-lg"><FaYoutube /></a>
              <a href="https://wa.me/14698779235" target="_blank" rel="noreferrer" className="text-white/25 hover:text-green-400 transition-colors text-xs">WhatsApp</a>
              <span className="text-white/15 text-xs">+1-469-877-9235</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
